<template>
<header class="page-header-2 page-header-fixed">
  <div class="page-header-2__top-section">
    <div class="page-header-2__top-section-content static-container">
      <nav class="flex">
        <ul class="flex items-center hidden lg:flex">
          <li
            v-for="mainMenuLink in mainMenuLinks"
            class="mx-2 text-dark-light"
          >
            <router-link :to="mainMenuLink.to">{{ mainMenuLink.text }}</router-link>
          </li>
        </ul>
      </nav>
      <div class="flex-grow"/>
      <div class="flex items-center mx-2">
        <CallbackFormModal
          v-if="activeCityPhone"
          :phone="activeCityPhone"
        >
          <template #toggle>
            <button
              class="flex p-1"
              type="button"
            >
              <span
                style="font-weight: inherit;"
              >{{ activeCityPhone.national }}</span>
              <!-- <span class="font-bold hidden sm:inline">{{ activeCityPhone.national }}</span>
              <span class="sm:hidden"><i class="fa fa-phone"/></span> -->
              <i class="far fa-angle-down ml-2 self-center text-dark-light"/>
            </button>
          </template>
        </CallbackFormModal>
      </div>
      <div class="flex items-center mx-2">
        <HeaderCitySelect
          v-if="displayCitySelect"
          v-on="citySelectProps.listeners"
          v-bind="citySelectProps.props"
        >
          <template #toggle="{ activeCity }">
            <button
              type="button"
              :aria-label="`Город${activeCity && (', ' + activeCity.name) || ''}`"
              class="flex items-center p-1"
              style="font-weight: inherit;"
            >
              <IconMapMarker class="w-3 fill-current text-primary"/>
              <span
                v-if="activeCity"
                class="ml-1"
              >{{ activeCity.name }}</span>
            </button>
          </template>
        </HeaderCitySelect>
      </div>
      <p class="flex items-center mx-2 p-1">
        <i class="fal fa-clock text-dark-light"/>
        <span class="ml-1">{{ scheduleStr }}</span>
        <span
          :title="storeOpenNow ? 'Открыто' : 'Закрыто'"
          :aria-label="storeOpenNow ? 'Открыто' : 'Закрыто'"
          class="block ml-1 rounded-full"
          style="width: 6px; height: 6px;"
          :class="{ [storeOpenNow ? 'bg-success' : 'bg-grey']: true }"
        ></span>
      </p>
      <div
        v-if="authenticationEnabled"
        class="flex items-center mx-2"
      >
        <HeaderUserModal :authentication-enabled="authenticationEnabled">
          <template #toggle="{ authenticated, active, onClick, user }">
            <button
              @click="onClick"
              class="flex items-center text-primary p-2"
              :class="{ 'text-menu-text-hover': active }"
              style="font-weight: inherit;"
            >
              <IconUser class="fill-current h-3"/>
              <span class="ml-1">
                <template v-if="authenticated">{{ user.full_name || 'Личный кабинет' }}</template>
                <template v-else>Войти</template>
              </span>
            </button>
          </template>
        </HeaderUserModal>
      </div>
    </div>
  </div>
  <div class="page-header-2__bottom-section">
    <div class="page-header-2__bottom-section-content static-container">
      <router-link
        :to="{ name: 'home' }"
        aria-label="Домашняя страница"
        class="page-header-2__home-link"
      >
        <img :src="$store.getters['settings/headerLogo']" alt="Лого">
      </router-link>

      <nav
        v-if="!searchIsActive"
        class="page-header-2__categories-container-wrapper"
      >
        <ul
          v-bind-client-width="categoriesContainerElClientWidth"
          ref="categoriesContainer"
          class="page-header-2__categories-container"
        >
          <li
            v-for="c of categoriesDistributed.inline"
            :key="c.id"
            class="page-header-2__category"
          >
            <HeaderCategoriesDropdown
              v-if="c.children && c.children.length > 0"
              :child-categories="c.children"
              :active="openCategoryDropdowns[c.id] || false"
              @update:active="($event ? $set : $delete)(openCategoryDropdowns, c.id, $event)"
            >
              <template #toggle>{{ c.name }}</template>
            </HeaderCategoriesDropdown>
            <router-link
              v-else
              class="p-2"
              :to="{ name: 'catalog', params: { categoryId: c.short_id, categorySlug: c.slug } }"
            >{{ c.name }}</router-link>
          </li>
          <li class="page-header-2__category">
            <HeaderCategoriesDropdown
              v-if="categoriesDistributed.dropdownMenu.length > 0"
              :child-categories="categoriesDistributed.dropdownMenu"
              :active="openCategoryDropdowns['more'] || false"
              @update:active="($event ? $set : $delete)(openCategoryDropdowns, 'more', $event)"
            >
              <template #toggle>Ещё</template>
            </HeaderCategoriesDropdown>
          </li>
        </ul>
      </nav>
      <div
        class="page-header-2__search"
        :class="{ 'flex-grow': searchIsActive }"
      >
        <Search
          :active.sync="searchIsActive"
          @clear="searchIsActive = false"
        />
      </div>
      <CartButton
        class="page-header-2__cart-button"
        @click="() => $store.commit('ui/toggleCartModal')"
      />
    </div>
  </div>
</header>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import debounce from 'lodash/debounce';
import mixin from './mixin';
import scheduleObjToString from '@/helpers/scheduleObjToString';
import HasMainMenuLinks from "@/mixins/HasMainMenuLinks";

export default {
  mixins: [mixin, HasMainMenuLinks],
  components: {
    CallbackFormModal: () => import('@/components/modals/CallbackFormModal'),
    HeaderUserModal: () => import('@/components/headers/HeaderUserModal'),
    HeaderCitySelect: () => import('./HeaderCitySelect'),
    Search: () => import(/* webpackPreload: true */ '@/components/search/Search'),
    HeaderCategoriesDropdown: () => import('./HeaderCategoriesDropdown'),
    IconUser: () => import('@/assets/images/icons/icon-user.svg?inline'),
    IconMapMarker: () => import('@/assets/images/icons/icon-map-marker.svg?inline'),
    CartButton: () => import(/* webpackPreload: true */ '@/components/headers/CartButton'),
  },
  directives: {
    bindClientWidth: {
      bind(el, binding, vnode) {
        vnode.context.$data[binding.expression] = el.clientWidth;
        const observer = new ResizeObserver(entries => {
          vnode.context.$data[binding.expression] = entries[0].contentRect.width;

          if (process.env.NODE_ENV !== 'production') {
            const difference = vnode.context.$data[binding.expression] - entries[0].target.clientWidth;
            if (Math.abs(difference) > 1) {
              console.error('I thought `contentRect.width` and `target.clientWidth` are the same.'
                + ' Might want to replace the former with the latter');
            }
          }
        })
        observer.observe(el);
      },
      unbind(el, binding, vnode) {
        vnode.context.$data[binding.expression] = null;
      }
    },
  },

  created() {
    this.$watch(
      () => [this.debouncedCategoriesContainerElClientWidth, this.visibleCategoriesTree],
      () => this.distributeCategories(),
      { immediate: true },
    );
  },

  computed: {
    ...mapState({
      visibleCategoriesTree: state => state.catalog.visibleCategoriesTree,
    }),
    ...mapGetters({
      activeCityPhone: 'store/activeCityPhone',
      authenticationEnabled: 'store/authenticationEnabled',
    }),
    schedule() {
      return this.$store.getters['store/activeCity']?.schedule;
    },
    storeOpenNow() {
      return this.schedule?.today.open;
    },
    scheduleStr() {
      return this.schedule && scheduleObjToString(this.schedule, '–');
    },
  },

  methods: {
    async distributeCategories() {
      const el = this.$refs.categoriesContainer;
      if (!el) return;

      this.cancelLastDistributeCategories?.();
      let cancelled = false;
      this.cancelLastDistributeCategories = () => cancelled = true;

      this.categoriesDistributed = {
        inline: [],
        dropdownMenu: [...this.visibleCategoriesTree],
      };
      const { inline, dropdownMenu } = this.categoriesDistributed;
      // Fill the inline container with categories until it overflows.
      while (true) {
        if (cancelled) break;
        if (dropdownMenu.length === 0) break;
        inline.push(dropdownMenu.shift());
        await this.$nextTick();
        const overflown = el.scrollWidth > el.parentElement.clientWidth;
        if (overflown) {
          dropdownMenu.unshift(inline.pop()); // Cancel one as we've just overflown the container.
          break;
        }
      }

      this.cancelLastDistributeCategories = null;
    },
  },

  watch: {
    categoriesContainerElClientWidth: {
      immediate: true,
      handler: debounce(
        function (n) {
          this.debouncedCategoriesContainerElClientWidth = n;
        },
        200
      ),
    },
  },

  data() {
    return {
      categoriesContainerElClientWidth: null, // `null` when the element isn't there.
      debouncedCategoriesContainerElClientWidth: null,
      cancelLastDistributeCategories: null,
      categoriesDistributed: {
        inline: [],
        dropdownMenu: [],
      },
      openCategoryDropdowns: {},
    }
  },
}
</script>

<style lang="less">
@import './common';

.page-header-2 {
  #page-headers-common.header();
  @top-section-height: theme('height.8'); // According to mockup, it must be 10.
  @bottom-section-height: calc(var(--header-height) - @top-section-height);
  &__top-section {
    @apply bg-light px-4; // `px-8` according to mockup.
  }
  &__top-section-content {
    @apply text-xs font-light flex;
    height: @top-section-height;
    :last-child {
      @apply mr-0; // Using the section padding for this instead.
    }
  }
  @bottom-section-padding-x: theme('padding.4'); // `px-8`
  &__bottom-section {
    @apply bg-white;
    padding: 0 @bottom-section-padding-x;
  }
  &__bottom-section-content {
    @apply flex justify-between;
    height: @bottom-section-height;
  }
  &__home-link {
    @apply flex-shrink-0 flex px-2 py-2;
    height: @bottom-section-height; // So when the image is big, it doesn't stretch the whole parent.
    > img {
      height: 100%;
    }
  }
  &__categories-container-wrapper {
    @apply overflow-hidden flex-grow flex items-center;
  }
  &__categories-container {
    @apply flex flex-grow justify-center;
  }
  &__category {
    @apply mx-2 font-light flex items-center;
    white-space: nowrap;
  }
  &__search {
    @apply px-4 flex items-center;
    > * {
      @apply flex-grow;
    }
  }
  &__cart-button {
    @margin: theme('margin.1');
    @apply flex-shrink-0 justify-between;
    margin: @margin;
    margin-right: calc(-1 * @bottom-section-padding-x + @margin);
    min-width: theme('width.24'); // To reduce content shift during loading.
  }
}
</style>
